<template>
  <div class="edit-field divide-y text-headline">
    <div class="flex items-center justify-between">
      <span class="text-lg font-bold">
        {{ allFieldDetails.name }}
      </span>
      <div class="flex items-center justify-end space-x-3">
        <button
          class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-sm border text-gray-400 hover:text-red-500"
          data-cy="delete-button"
          @click.prevent="showDeleteModal = true"
        >
          <icon-base :icon="IconTrashCanOutline" />
        </button>
        <div class="cursor-pointer" @click="$emit('close')">
          <icon-base :icon="IconX" />
        </div>
      </div>
    </div>
    <div class="font-semibold">
      {{ $t("CONTRACTS.CONTRACT_BUILDER.PROPERTIES") }}
    </div>
    <div v-if="field.tabLabel === CUSTOM_ATTRIBUTE">
      <lf-dropdown-with-api-search
        ref="attributeDropdown"
        :placeholder="$t('ATTRIBUTE_BUILDER.ATTRIBUTE')"
        name="attribute"
        :value="field.context?.value.id"
        :get-paginated-options="getPublishedAttributes"
        accessor="name"
        full-width
        @change="editAttribute"
      />
    </div>
    <div class="space-y-3">
      <lf-dropdown
        key="role_edit"
        :placeholder="$t('COMMON.ROLE')"
        name="role_edit"
        :value="selectedRole.role"
        :options="roles"
        :option-formatter="formatRoleOptions"
        full-width
        @change="editRole"
      >
        <template #type>
          <span
            class="ml-2 rounded-4 bg-gray-200 px-2 text-xxs font-semibold uppercase"
            data-cy="role-index"
          >
            {{ getCountOfRole(allFieldDetails.role, roles) }}
          </span>
        </template>
      </lf-dropdown>
      <div class="flex items-center justify-between space-x-2">
        <div>{{ $t("COMMON.FIELD_REQUIRED") }}</div>
        <lf-switch
          v-model="allFieldDetails.required"
          name="role_required"
          :label="$t('REQUIRED')"
          @update:model-value="$emit('field:edit', allFieldDetails)"
        />
      </div>
    </div>
    <div class="">{{ $t("COMMON.LOCATION") }}</div>
    <div class="grid w-full grid-cols-2 items-center gap-y-4">
      <div>{{ $t("CONTRACTS.CONTRACT_BUILDER.ON_PAGE") }}</div>
      <div class="justify-self-end">{{ allFieldDetails.pageNumber }}</div>
      <div>{{ $t("CONTRACTS.CONTRACT_BUILDER.PIXELS_FROM_LEFT") }}</div>
      <div class="items-center justify-self-end">
        <lf-input
          :value="allFieldDetails.xPosition"
          type="number"
          step="1"
          :placeholder="$t('CONTRACTS.CONTRACT_BUILDER.X_POSITION')"
          name="x"
          class="w-40"
          no-margin
          data-cy="x-input"
          @change.stop="editPosition('xPosition', $event.target.value)"
        />
      </div>
      <div>{{ $t("CONTRACTS.CONTRACT_BUILDER.PIXELS_FROM_TOP") }}</div>
      <div class="justify-self-end">
        <lf-input
          :value="allFieldDetails.yPosition"
          type="number"
          step="1"
          :placeholder="$t('CONTRACTS.CONTRACT_BUILDER.Y_POSITION')"
          name="y"
          class="w-40"
          no-margin
          data-cy="y-input"
          @change.stop="editPosition('yPosition', $event.target.value)"
        />
      </div>
    </div>
  </div>
  <confirm-modal
    v-if="showDeleteModal"
    :title="`${$t('COMMON.DELETE')} ${$t('COMMON.FIELD')}`"
    :description="
      $t('COMMON.DELETE_CONFIRMATION', { source: $t('COMMON.FIELD') })
    "
    :confirm-button="$t('COMMON.DELETE')"
    confirm-button-type="error"
    :close="() => (showDeleteModal = false)"
    @confirm="handleDelete"
  />
</template>

<script setup lang="ts">
import { type PropType, computed, ref, watch } from "vue";

import omit from "lodash/omit";

import { CustomAttributeStatusOption } from "@/enums/customAttributes";
import { CUSTOM_ATTRIBUTE } from "@/helpers/constants/contractBuilder";
import { formatRoleOptions, getCountOfRole } from "@/helpers/contractBuilder";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { usePromiseWrapper } from "@/hooks/common";
import { useContractBuilder } from "@/hooks/contractBuilder";
import attributeBuilderService from "@/services/modules/attributeBuilder";

import IconTrashCanOutline from "@/components/icons/IconTrashCanOutline.vue";
import IconX from "@/components/icons/deals/IconX.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";

import type {
  ContractField,
  ContractFieldWithName,
  RoleWithID,
} from "@/models/contractBuilder";
import type { CustomAttribute } from "@/models/customAttributes/customAttribute";

const emit = defineEmits<{
  "field:edit": [ContractFieldWithName];
  "field:delete": [ContractFieldWithName];
  close: [];
}>();

const props = defineProps({
  field: {
    type: Object as PropType<ContractField>,
    required: true,
  },
  roles: {
    type: Array as PropType<RoleWithID[]>,
    required: true,
  },
  selectedRole: {
    type: Object as PropType<RoleWithID>,
    required: true,
  },
  attributes: {
    type: Array as PropType<CustomAttribute[]>,
  },
});

const { contractTemplatesOptions } = useContractBuilder();
const { isLendflowUser } = useAuth();
const { activeClient } = useClients();

const attributeDropdown = ref<InstanceType<
  typeof LfDropdownWithApiSearch
> | null>(null);

const canManageAttributeBuilder = computed(
  () => activeClient.value?.can_manage_attribute_builder
);

const canAccessAttributeBuilder = computed(() => {
  return canManageAttributeBuilder.value || isLendflowUser;
});

const allFieldDetails = ref<ContractFieldWithName>({
  ...props.field,
  role: props.selectedRole,
});
const showDeleteModal = ref(false);

const handleDelete = () => {
  emit("field:delete", allFieldDetails.value);
};

const { fetchWrapper: getPublishedAttributes } = usePromiseWrapper(async () => {
  if (!canAccessAttributeBuilder.value) {
    return;
  }
  return await attributeBuilderService.getAttributes({
    status: CustomAttributeStatusOption.published,
  });
});

const mergeDetails = () => {
  const matchedField = contractTemplatesOptions.value?.fields
    .flatMap((category) => category.fields)
    .find((templateField) => templateField.tab_label === props.field.tabLabel);
  allFieldDetails.value = {
    ...props.field,
    role: props.selectedRole,
    ...omit(matchedField, ["tab_label"]),
  };
};

const editRole = (role: number) => {
  allFieldDetails.value.role = props.roles[role];
  emit("field:edit", allFieldDetails.value);
};

const editAttribute = (attribute: CustomAttribute) => {
  if (!attribute.id) {
    return;
  }
  allFieldDetails.value.context = {
    type: CUSTOM_ATTRIBUTE,
    value: { id: attribute.id },
  };
  emit("field:edit", allFieldDetails.value);
};

const editPosition = (key: "xPosition" | "yPosition", position: number) => {
  allFieldDetails.value[key] = Math.round(Number(position));
  emit("field:edit", allFieldDetails.value);
};

watch(() => props.field, mergeDetails, { immediate: true, deep: true });
watch(
  () => attributeDropdown.value,
  (newValue, oldValue) => {
    if (!newValue) {
      return;
    }
    if (newValue !== oldValue) {
      // we need the options to show the proper value
      newValue.loadMore();
    }
  }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.edit-field > div {
  @apply p-6;
}
</style>
